import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "./ToastProvider";
import { requireAuth } from "../utils/requireAuth";
import discountIcon from "../assets/discount.svg";
import Rectangle1 from "../assets/sainsbury1.jpeg";
import Rectangle2 from "../assets/tesco.jpeg";
import Rectangle3 from "../assets/asda.jpeg";
import Rectangle4 from "../assets/morrisons.jpeg";

// Same retailer banners as `ProductDetail` / home `SupermarketTabs` (Rectangle1–4).
const retailerBannerByPlatform = {
  sainsbury: Rectangle1,
  tesco: Rectangle2,
  asda: Rectangle3,
  morrison: Rectangle4,
};

const OFFER_COMPARE_PLATFORM_ORDER = ["tesco", "asda", "sainsbury", "morrison"];

function firstOfferTextFromItem(item) {
  const offers = Array.isArray(item?.offers) ? item.offers : [];
  const x = offers[0];
  if (x && typeof x === "object") {
    const text = x.description || x.desc || x.longDescription || x.title || x.text || "";
    if (text) return String(text).trim();
  }
  return String(
    item?.promotion ||
      item?.offer_line ||
      item?.promotional_text ||
      item?.offer_text ||
      item?.promo_text ||
      item?.multi_buy ||
      item?.deal_text ||
      ""
  ).trim();
}

function InfoIcon({ tooltip }) {
  if (!tooltip) return null;
  return (
    <span className="relative group inline-flex shrink-0 text-[rgb(176,48,28)]">
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 10v6" strokeLinecap="round" />
        <circle cx="12" cy="7" r="1" fill="currentColor" stroke="none" />
      </svg>
      <span className="pointer-events-none absolute right-0 top-full z-50 mt-2 w-max max-w-[16rem] rounded-lg border border-[rgb(176,48,28)]/40 bg-white px-2.5 py-1.5 text-[12px] font-medium text-[rgb(176,48,28)] opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
        {tooltip}
      </span>
    </span>
  );
}

function formatRelativeUpdated(product) {
  const scrapeTime = product?.scrape_time;
  if (!scrapeTime) return null;
  const now = new Date();
  const today = now.toISOString().split("T")[0];
  const scrapeDate = new Date(`${today}T${scrapeTime}`);
  if (isNaN(scrapeDate.getTime())) return null;
  let diffSec = Math.floor((now - scrapeDate) / 1000);
  if (diffSec < 0) diffSec += 24 * 60 * 60;
  if (diffSec < 45) return "Updated just now";
  const min = Math.floor(diffSec / 60);
  if (min < 60) return `Updated ${min} min ago`;

  const hr = Math.floor(min / 60);
  if (hr < 24) return `Updated ${hr} hr ago`;

  const day = Math.floor(hr / 24);
  return `Updated ${day} day${day === 1 ? "" : "s"} ago`;
}

function ProductCard({
  accent,
  product,
  onClick,
  onAddToList,
  retailerLabel,
  pricingMode = "Base",
  showComparison = true,
}) {
  void accent;
  const [addBusy, setAddBusy] = useState(false);
  const { showToast } = useToast();
  const navigate = useNavigate();

  const productMatchType =
    String(
      product?.response?.match_type ??
        product?.match_type ??
        ""
    ).trim() || "";
  // For listing-style cards (single retailer price), the product itself represents one platform.
  const isProductHigherSizeMatch = productMatchType === "Higher size Match";
  const productNameForTooltip = String(product?.response?.product_name ?? product?.product_name ?? "").trim();

  const normalizePlatformKey = (platform) => {
    const p = String(platform || "").trim().toLowerCase();
    if (p === "morrisons") return "morrison";
    if (p === "sainsburry") return "sainsbury";
    if (p === "sainsburys") return "sainsbury";
    return p;
  };

  const parsePrice = (value) => {
    if (value == null) return null;
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    const normalized = String(value).trim().replace(",", ".").replace(/[^0-9.-]/g, "");
    if (!normalized) return null;
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const platformMeta = {
    tesco: { label: "Tesco", priceColor: "#5B3CC4" },
    asda: { label: "ASDA", priceColor: "#5B3CC4" },
    morrison: { label: "Morrisons", priceColor: "#5B3CC4" },
    sainsbury: { label: "Sainsbury's", priceColor: "#5B3CC4" },
  };

  const knownPlatformKey = normalizePlatformKey(product?.platform);
  const knownStoreMeta = platformMeta[knownPlatformKey] || null;

  const retailerLabelToPlatformKey = (label) => {
    const l = String(label || "").trim().toLowerCase();
    if (l.includes("tesco")) return "tesco";
    if (l.includes("morris")) return "morrison";
    if (l.includes("asda")) return "asda";
    if (l.includes("sainsbury")) return "sainsbury";
    return "";
  };

  const effectivePlatformKey = retailerLabel
    ? retailerLabelToPlatformKey(retailerLabel)
    : knownPlatformKey;

  const comparisonSource =
    Array.isArray(product?.comparison) && product.comparison.length > 0
      ? product.comparison
      : ["tesco", "morrison", "asda", "sainsbury"].map((platform) => ({
          platform,
          price: platform === effectivePlatformKey ? product?.price : null,
        }));

  const isOfferLoyaltyCompare = showComparison && pricingMode === "Loyalty+Offer";
  const hideAddToList = /offer|loyalty/i.test(String(pricingMode || ""));

  const comparisonRows = comparisonSource.map((item) => {
    const platform = normalizePlatformKey(
      item?.platform || item?.retailer || item?.provider || item?.store || item?.site
    );
    const meta = platformMeta[platform] || {
      label: item?.platform || item?.retailer || item?.provider || item?.store || "Store",
      priceColor: "#5B3CC4",
    };
    const basePrice = parsePrice(item?.price ?? item?.unit_price ?? item?.price_per_unit);
    const offerPrice =
      parsePrice(item?.offer_price) ??
      parsePrice(item?.loyalty_pricing) ??
      (() => {
        const offers = Array.isArray(item?.offers) ? item.offers : [];
        return parsePrice(offers[0]?.offer_price ?? offers[0]?.price);
      })();
    const numericPrice = isOfferLoyaltyCompare
      ? offerPrice ?? basePrice
      : parsePrice(
          item?.price ?? item?.unit_price ?? item?.price_per_unit ?? item?.offer_price
        );
    const productName =
      String(item?.product_name ?? "").trim() ||
      "";
    const rowMatchType =
      String(item?.match_type ?? "")
        .trim() || "";
    const promotion = firstOfferTextFromItem(item);
    return {
      platform,
      store: meta.label,
      price: Number.isFinite(numericPrice) ? numericPrice : null,
      basePrice: Number.isFinite(basePrice) ? basePrice : null,
      offerPrice: Number.isFinite(offerPrice) ? offerPrice : null,
      promotion,
      formatted: Number.isFinite(numericPrice) ? `£${numericPrice.toFixed(2)}` : "—",
      priceColor: meta.priceColor,
      match_type: rowMatchType,
      product_name: productName,
    };
  });

  const knownPrices = comparisonRows
    .map((r) => r.price)
    .filter((p) => typeof p === "number" && Number.isFinite(p));

  const cheapestPrice = knownPrices.length ? Math.min(...knownPrices) : null;
  const highestPrice = knownPrices.length ? Math.max(...knownPrices) : null;
  // Show lowest-price badge on every store at the min price only when someone is higher.
  // If all known prices are equal, hide the badge entirely.
  const hasPriceSpread =
    cheapestPrice != null && highestPrice != null && highestPrice > cheapestPrice;
  const isLowestPrice = (row) =>
    hasPriceSpread && row?.price != null && row.price === cheapestPrice;

  const sortedComparisonRows = (() => {
    const rows = Array.isArray(comparisonRows) ? [...comparisonRows] : [];
    if (isOfferLoyaltyCompare) {
      return OFFER_COMPARE_PLATFORM_ORDER.map((platform) =>
        rows.find((row) => row.platform === platform)
      ).filter(Boolean);
    }
    return rows.sort((a, b) => {
      const aIsCheapest = isLowestPrice(a);
      const bIsCheapest = isLowestPrice(b);
      if (aIsCheapest !== bIsCheapest) return aIsCheapest ? -1 : 1;

      const aPrice = typeof a.price === "number" && Number.isFinite(a.price) ? a.price : null;
      const bPrice = typeof b.price === "number" && Number.isFinite(b.price) ? b.price : null;
      if (aPrice == null && bPrice == null) return String(a.store || "").localeCompare(String(b.store || ""));
      if (aPrice == null) return 1;
      if (bPrice == null) return -1;
      if (aPrice !== bPrice) return aPrice - bPrice;
      return String(a.store || "").localeCompare(String(b.store || ""));
    });
  })();

  const potentialSavings =
    cheapestPrice != null && highestPrice != null ? highestPrice - cheapestPrice : null;
  const savings =
    typeof potentialSavings === "number" &&
    Number.isFinite(potentialSavings) &&
    potentialSavings > 0
      ? potentialSavings
      : null;

  const retailerLogoSrc = (platform) =>
    platform && retailerBannerByPlatform[platform] ? retailerBannerByPlatform[platform] : null;

  const updatedLabel = formatRelativeUpdated(product);
  const categoryLabel = product?.category ? String(product.category) : null;
  const sizeLabel = product?.packsize || product?.weight || null;

  const effectiveRow = comparisonRows.find((r) => r.platform === effectivePlatformKey);
  const basePriceNumber =
    parsePrice(product?.price) ??
    parsePrice(effectiveRow?.price) ??
    null;

  const offerPriceNumber = (() => {
    const offers = Array.isArray(product?.offers) ? product.offers : [];
    const first = offers[0];
    return (
      parsePrice(first?.offer_price) ??
      parsePrice(product?.offer_price) ??
      null
    );
  })();

  const loyaltyPriceNumber =
    parsePrice(product?.loyalty_pricing) ??
    null;

  const chosenPriceNumber = (() => {
    const mode = String(pricingMode || "Base");
    if (mode === "Offer") return offerPriceNumber ?? basePriceNumber;
    if (mode === "Loyalty+Offer") {
      return loyaltyPriceNumber ?? offerPriceNumber ?? basePriceNumber;
    }
    return basePriceNumber;
  })();

  const displayPriceFormatted =
    chosenPriceNumber != null ? `£${chosenPriceNumber.toFixed(2)}` : "£—";

  const hasValidPrice = isOfferLoyaltyCompare
    ? comparisonRows.some((r) => r.basePrice != null || r.offerPrice != null || r.price != null)
    : chosenPriceNumber != null ||
      (typeof effectiveRow?.price === "number" && Number.isFinite(effectiveRow.price)) ||
      parsePrice(product?.price) != null;

  const promoText = String(product?.promotion || "").trim();
  const showPromoBanner = !showComparison && promoText.length > 0;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={hasValidPrice ? onClick : undefined}
      onKeyDown={(e) => {
        if (!hasValidPrice) return;
        if (e.key === "Enter" || e.key === " ") onClick?.();
      }}
      className={[
        "flex h-full min-h-0 flex-col rounded-[18px] border border-[#E6EAF0] bg-white text-left shadow-sm transition-shadow focus:outline-none",
        hasValidPrice ? "hover:shadow-md" : "opacity-70 cursor-not-allowed",
      ].join(" ")}
    >
      <div className="flex min-h-0 flex-1 flex-col p-4">
        {/* Image */}
        <div className="mb-3 flex h-[140px] items-center justify-center rounded-xl bg-[#FFFFFF] sm:h-[200px]">
          {product?.images?.[0] ? (
            <img
              src={product.images[0]}
              alt={product.title || product.sku}
              className="h-full w-full object-contain"
            />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center gap-2 rounded-xl border border-[#E6EAF0] bg-slate-50">
              <svg
                width="34"
                height="34"
                viewBox="0 0 24 24"
                fill="none"
                className="text-slate-400"
                aria-hidden
              >
                <path
                  d="M21 16.2V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v11.2"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
                <path
                  d="M3.5 18.5l4.7-4.7a1.5 1.5 0 0 1 2.1 0l2.4 2.4a1.5 1.5 0 0 0 2.1 0l1.2-1.2a1.5 1.5 0 0 1 2.1 0L21 18.2"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10.5 8.5h.01"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
              <div className="text-center">
                <div className="text-[16px] font-semibold text-slate-600">Image unavailable</div>
                <div className="text-[14px] font-medium text-slate-400">We’ll update it soon</div>
              </div>
            </div>
          )}
        </div>

        <p className="font-semibold text-[#24303A] line-clamp-2 min-h-[36px] text-[18px] sm:min-h-[40px]">
          {product?.title}
        </p>

        <div className="mt-2 flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
          <div className="flex flex-wrap items-center gap-2">
            {categoryLabel && (
              <span className="inline-flex rounded-full bg-[#2EB5C9] px-2.5 py-0.5 text-[14px] font-semibold text-[#FFFFFF]">
                {categoryLabel}
              </span>
            )}
            {sizeLabel && (
              <span className="inline-flex rounded-full bg-[#F59E0B] px-2.5 py-0.5 text-[14px] font-semibold text-[#FFFFFF]">
                {String(sizeLabel)}
              </span>
            )}
          </div>
          {updatedLabel && (
            <span className="shrink-0 text-[14px] font-normal text-[#6E7B86]">{updatedLabel}</span>
          )}
        </div>

        {showComparison ? (
          <>
            <div className="mt-2 overflow-visible rounded-xl border border-[#E6EAF0] bg-white text-[15px] sm:text-[16px]">
              {isOfferLoyaltyCompare
                ? sortedComparisonRows?.map((row, idx) => {
                    const hasOfferPrice = row.offerPrice != null;
                    const infoTooltip =
                      row.match_type === "Higher size Match" && row.product_name
                        ? row.product_name
                        : row.product_name || row.store;
                    return (
                      <div key={row.platform}>
                        {idx !== 0 && <div className="h-px bg-[#EEF2F7]" aria-hidden />}
                        <div className="flex flex-col gap-2 px-3 py-2.5">
                          <div className="flex flex-col gap-2 min-[340px]:flex-row min-[340px]:items-center min-[340px]:justify-between min-[340px]:gap-3">
                            {retailerLogoSrc(row.platform) ? (
                              <span className="flex h-10 w-24 shrink-0 items-center justify-center overflow-hidden rounded-md border border-slate-300 bg-slate-50 pt-1">
                                <img
                                  src={retailerLogoSrc(row.platform)}
                                  alt={row.store}
                                  className="w-full object-cover object-center"
                                  loading="lazy"
                                />
                              </span>
                            ) : (
                              <span className="min-w-0 truncate font-semibold text-[#24303A] text-[16px]">
                                {row.store}
                              </span>
                            )}
                            <div className="flex items-center justify-end gap-2 sm:shrink-0">
                              <span className="font-semibold tabular-nums text-[#7352C7] text-[16px]">
                                {hasOfferPrice ? `£${row.offerPrice.toFixed(2)}` : "—"}
                              </span>
                              <InfoIcon tooltip={infoTooltip} />
                            </div>
                          </div>
                          {hasOfferPrice ? (
                            <p className="font-semibold text-[#284283] text-[16px]">
                              {row.promotion || `£${row.offerPrice.toFixed(2)} offer price`}
                            </p>
                          ) : (
                            <div className="space-y-1">
                              {row.basePrice != null && (
                                <p className="font-semibold text-[#284283] text-[16px]">
                                  Regular price £{row.basePrice.toFixed(2)}
                                </p>
                              )}
                              <p className="font-semibold text-[#6E7B86] text-[16px]">
                                No offer price available
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                : sortedComparisonRows?.map((row, idx) => {
                const isBest = isLowestPrice(row);
                const isRowHigherSizeMatch = row?.match_type === "Higher size Match";
                const priceClass = isRowHigherSizeMatch
                  ? "text-[rgb(176,48,28)]"
                  : isBest
                  ? "text-[#2FC98D]"
                  : "text-[#7352C7]";
                return (
                  <div key={row.platform}>
                    {idx !== 0 && <div className="h-px bg-[#EEF2F7]" aria-hidden />}
                    <div
                      className={[
                        "relative gap-1 py-2.5 border",
                        isBest ? "bg-[#EAFBF3] border-[#2FC98D80]" : "bg-white border-transparent",
                        idx === 0 ? "rounded-t-xl" : "",
                        "flex flex-col",
                        isBest ? "pl-8 pr-3" : "px-3",
                      ].join(" ")}
                    >
                      {isBest && (
                        <span className="absolute left-0 top-0">
                          <span className="relative inline-flex h-7 w-7 items-center justify-center">
                            <span
                              className="absolute inset-0 rounded-br-[10px] rounded-tr-none rounded-l-none bg-[#5B3CC4]"
                              aria-hidden
                            />
                            <svg
                              viewBox="0 0 24 24"
                              className="relative z-10 h-[24px] w-[24px]"
                              fill="none"
                              aria-hidden
                            >
                              <path
                                d="M12 3.2l2.55 5.17 5.7.83-4.12 4.02.97 5.68L12 16.48 6.9 18.92l.97-5.68L3.75 9.2l5.7-.83L12 3.2z"
                                fill="#FFFFFF"
                              />
                            </svg>
                          </span>
                        </span>
                      )}
                      <div className="flex flex-col gap-2 min-[340px]:flex-row min-[340px]:items-center min-[340px]:justify-between min-[340px]:gap-3">
                        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 sm:gap-3">
                        {retailerLogoSrc(row.platform) ? (
                          <span className="flex h-10 w-24 shrink-0 pt-1 items-center justify-center overflow-hidden rounded-md border border-slate-300 bg-slate-50">
                            <img
                              src={retailerLogoSrc(row.platform)}
                              alt={row.store}
                              className="w-full object-cover object-center"
                              loading="lazy"
                            />
                          </span>
                        ) : (
                          <span
                            className={[
                              "min-w-0 truncate font-semibold",
                              isRowHigherSizeMatch ? "text-[rgb(176,48,28)]" : "text-[#24303A]",
                            ].join(" ")}
                          >
                            {row.store}
                          </span>
                        )}

                        {isBest && (
                          <span className="shrink-0 rounded-full bg-[#1FA971] px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-white sm:px-3.5 sm:py-1 sm:text-[11px]">
                            LOWEST PRICE
                          </span>
                        )}

                        {isRowHigherSizeMatch && String(row?.product_name || "").trim() && (
                          <span className="relative group inline-flex shrink-0 text-[rgb(176,48,28)]">
                            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="9" />
                              <path d="M12 10v6" strokeLinecap="round" />
                              <circle cx="12" cy="7" r="1" fill="currentColor" stroke="none" />
                            </svg>
                            <span className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 w-max max-w-[16rem] -translate-x-1/2 rounded-lg border border-[rgb(176,48,28)]/40 bg-white px-2.5 py-1.5 text-[12px] font-medium text-[rgb(176,48,28)] opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                              {row.product_name}
                            </span>
                          </span>
                        )}
                        </div>

                        <div className="flex items-center justify-end gap-2 sm:shrink-0">
                        <span className={["font-semibold tabular-nums text-[16px]", priceClass].join(" ")}>
                          {row.formatted}
                        </span>
                      </div>
                      </div>

                      {isBest && savings != null && (
                        <div className="text-[15px] font-semibold leading-snug text-[#E44A77]">
                          Save £{savings.toFixed(2)} vs highest price
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <>
            {showPromoBanner && (
              <div className="mt-3 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50/90 px-3 py-1.5">
                <img
                  src={discountIcon}
                  alt=""
                  width={18}
                  height={18}
                  className="h-[18px] w-[18px] shrink-0 object-contain"
                  aria-hidden
                />
                <p className="text-[14px] font-semibold text-[#2FC98D]">
                  {promoText}
                </p>
              </div>
            )}

            <div className="mt-3 flex items-center justify-between gap-3 text-[15px] sm:text-[16px]">
              <span className="flex min-w-0 items-center gap-1.5 text-[15px] sm:text-[16px]">
                {retailerLogoSrc(effectivePlatformKey) ? (
                  <span className="inline-flex h-8 w-[6rem] shrink-0 items-center justify-center overflow-hidden rounded-md border border-[#E6EAF0] bg-white">
                    <img
                      src={retailerLogoSrc(effectivePlatformKey)}
                      alt={retailerLabel || knownStoreMeta?.label || "Store"}
                      className="max-h-full w-full object-cover object-center mt-1"
                      loading="lazy"
                    />
                  </span>
                ) : (
                  <span
                    className={[
                      "min-w-0 truncate font-normal",
                      isProductHigherSizeMatch ? "text-[#2EB5C9]" : "text-[#24303A]",
                    ].join(" ")}
                  >
                    {retailerLabel || knownStoreMeta?.label || "Store"}
                  </span>
                )}
                {isProductHigherSizeMatch && productNameForTooltip && (
                  <InfoIcon tooltip={productNameForTooltip} />
                )}
              </span>
              <span className="flex items-baseline gap-2">
                {basePriceNumber != null &&
                  chosenPriceNumber != null &&
                  chosenPriceNumber !== basePriceNumber && (
                    <span className="text-[14px] font-semibold text-[#6E7B86] line-through">
                      £{basePriceNumber.toFixed(2)}
                    </span>
                  )}
                <span
                  className={[
                    "text-[15px] font-semibold sm:text-[16px]",
                    isProductHigherSizeMatch ? "text-[#2EB5C9]" : "text-[#7352C7]",
                  ].join(" ")}
                >
                  {displayPriceFormatted}
                </span>
              </span>
            </div>
          </>
        )}

        <div
          className={[
            "mt-auto pt-4",
            hideAddToList ? "flex justify-center" : "grid grid-cols-2 gap-2",
          ].join(" ")}
        >
          <button
            type="button"
            disabled={!hasValidPrice}
            className={[
              "rounded-full bg-[#7352C71A] px-3 py-1.5 font-bold text-[#7352C7] shadow-sm disabled:cursor-not-allowed disabled:opacity-60 text-[16px]",
              hideAddToList ? "min-w-[10rem] px-6" : "",
            ].join(" ")}
            onClick={(e) => {
              e.stopPropagation();
              if (!hasValidPrice) return;
              onClick?.();
            }}
          >
            View details
          </button>
          {!hideAddToList && (
          <button
            type="button"
            disabled={addBusy || !hasValidPrice}
            className="rounded-full bg-[#7352C7] px-3 py-1.5 font-bold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-60 text-[16px]"
            onClick={async (e) => {
              e.stopPropagation();
              if (addBusy || !hasValidPrice) return;
              if (!requireAuth("login")) return;
              if (typeof onAddToList !== "function") {
                showToast({
                  type: "error",
                  title: "Add failed",
                  message: "Add to list is not configured on this page yet.",
                });
                return;
              }
              setAddBusy(true);
              try {
                await onAddToList(product);
                showToast({
                  type: "success",
                  title: "Added to list",
                  message: "Product added to grocery list.",
                  actionLabel: "Go to Grocery List",
                  onAction: () => {
                    window.location.assign("/settings?tab=grocery&view=lists");
                  },
                });
              } catch (err) {
                const errorMessage = err?.message || "Failed to add product to list.";
                if (errorMessage === "ADD_TO_LIST_CANCELLED" || err?.code === "ADD_TO_LIST_CANCELLED") {
                  return;
                }
                const isNoListError = /No grocery list found/i.test(errorMessage);
                showToast({
                  type: "error",
                  title: "Add failed",
                  message: errorMessage,
                  actionLabel: isNoListError ? "Create list" : "",
                  onAction: isNoListError
                    ? () => navigate("/settings?tab=grocery&view=create")
                    : null,
                });
              } finally {
                setAddBusy(false);
              }
            }}
          >
            {addBusy ? "Adding..." : "Add to list"}
          </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProductCard;
